/**
 * PATH: apps/quote/src/components/builder/sheets/terms-sheet/TermsSheet.tsx
 * PURPOSE: Orchestrator for Terms & Conditions editor with templates + AI
 */

import { useState, useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Save, FileText, Sparkles, Copy, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { quoteApi, type QuoteDetail } from '@/lib/quoteApi'
import { RichTextEditor } from './RichTextEditor'
import {
  TemplateSelector,
  type TermsTemplate,
  type TemplateApplyMode,
} from './TemplateSelector'
import {
  appendLinesAsParagraphs,
  computeMissingLines,
} from './template-merge-helpers'

interface TermsSheetProps {
  quoteId: string
  quote: QuoteDetail
  isEditable: boolean
}

export default function TermsSheet({ quoteId, quote, isEditable }: TermsSheetProps) {
  const queryClient = useQueryClient()
  const [terms, setTerms] = useState(quote.terms_and_conditions || '')
  const [hasChanges, setHasChanges] = useState(false)
  const [isImproving, setIsImproving] = useState(false)

  const { data: templates = [], isLoading: isLoadingTemplates } = useQuery({
    queryKey: ['terms-templates', quote.organisation],
    queryFn: async () => {
      const response = await quoteApi.get<TermsTemplate[]>('/quotebuilder/terms-templates/', { params: { tenant: localStorage.getItem('CurrentTenantId') || '1' } })
      return response.data
    },
    staleTime: 5 * 60 * 1000,
  })

  const updateTermsMutation = useMutation({
    mutationFn: async (newTerms: string) => { await quoteApi.patch(`/quotebuilder/quotes/${quoteId}/`, { terms_and_conditions: newTerms }) },
    onSuccess: () => { setHasChanges(false); queryClient.invalidateQueries({ queryKey: ['quote', quoteId] }) },
  })

  const handleTermsChange = useCallback((html: string) => { setTerms(html); setHasChanges(html !== (quote.terms_and_conditions || '')) }, [quote.terms_and_conditions])

  // SAM-style template apply path: Replace overwrites the editor;
  // Add-missing only appends the lines in the template that aren't
  // already present in the current notes (computed by
  // computeMissingLines + appendLinesAsParagraphs, both pure I→O).
  const handleApplyTemplate = useCallback(
    async (template: TermsTemplate, mode: TemplateApplyMode) => {
      let rendered: string
      try {
        const response = await quoteApi.post<{ rendered_content: string }>(
          `/quotebuilder/terms-templates/${template.id}/render/`,
          { quote_id: quoteId },
        )
        rendered = response.data.rendered_content
      } catch {
        rendered = template.content
      }
      if (mode === 'replace') {
        setTerms(rendered)
        setHasChanges(true)
        return
      }
      const missing = computeMissingLines(terms, rendered)
      if (missing.length === 0) return
      setTerms((cur) => appendLinesAsParagraphs(cur, missing))
      setHasChanges(true)
    },
    [quoteId, terms],
  )

  const handleSave = useCallback(() => { updateTermsMutation.mutate(terms) }, [terms, updateTermsMutation])

  const handleAIImprove = useCallback(async () => {
    if (!terms) return
    setIsImproving(true)
    try {
      const response = await fetch('https://api.deepseek.com/v1/chat/completions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${import.meta.env.VITE_DEEPSEEK_API_KEY || ''}` },
        body: JSON.stringify({
          model: 'deepseek-v4-flash',
          messages: [
            { role: 'system', content: 'You are a legal document assistant for a construction subcontractor. Improve the following terms and conditions to be: 1. Clearer and more readable 2. More professional 3. Protective of the contractor\'s interests 4. Compliant with Australian construction law\n\nKeep the same structure (headings, lists) and length. Return only the improved HTML.' },
            { role: 'user', content: terms },
          ],
          temperature: 0.3, max_tokens: 4000,
        }),
      })
      if (response.ok) { const data = await response.json(); setTerms(data.choices?.[0]?.message?.content || terms); setHasChanges(true) }
    } catch (error) { console.error('AI improve failed:', error) } finally { setIsImproving(false) }
  }, [terms])

  const handleCopy = useCallback(() => {
    const tempDiv = document.createElement('div'); tempDiv.innerHTML = terms
    navigator.clipboard.writeText(tempDiv.textContent || tempDiv.innerText)
  }, [terms])

  return (
    <div className="h-full flex flex-col bg-white dark:bg-slate-950">
      <div className="flex-shrink-0 px-4 py-3 border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2"><FileText className="w-5 h-5 text-teal-500" />Terms & Conditions</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Legal terms and conditions for this quote</p>
          </div>
          <div className="flex items-center gap-2">{hasChanges && <span className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2 py-1 rounded">Unsaved changes</span>}</div>
        </div>
      </div>

      {isEditable && (
        <div className="flex-shrink-0 px-4 py-2 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TemplateSelector templates={templates} onSelect={handleApplyTemplate} isLoading={isLoadingTemplates} />
              <button onClick={handleAIImprove} disabled={!terms || isImproving} className={cn('flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors', 'bg-gradient-to-r from-violet-500 to-purple-500 text-white hover:from-violet-600 hover:to-purple-600', 'disabled:opacity-50 disabled:cursor-not-allowed')}>
                {isImproving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}<span>AI Improve</span>
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={handleCopy} disabled={!terms} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50" title="Copy to clipboard"><Copy className="w-4 h-4" /></button>
              <button onClick={handleSave} disabled={!hasChanges || updateTermsMutation.isPending} className={cn('flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors', hasChanges ? 'bg-teal-500 text-white hover:bg-teal-600' : 'bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed')}>
                {updateTermsMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}<span>Save</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4">
        <RichTextEditor value={terms} onChange={handleTermsChange} placeholder="Enter your terms and conditions here, or select a template above..." readOnly={!isEditable} />
        {isEditable && (
          <div className="mt-4 p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-700">
            <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">Available Variables (from templates)</p>
            <div className="flex flex-wrap gap-1.5">
              {['{client_name}', '{project_name}', '{quote_total}', '{quote_number}', '{valid_until}', '{estimator_name}'].map((variable) => (
                <code key={variable} className="px-1.5 py-0.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded text-xs text-slate-600 dark:text-slate-400">{variable}</code>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
